import { ref } from '@vue/composition-api'
import { groupBy, flatMap } from 'lodash'

// NOTE: 「非出勤者も表示」トグルの状態。
// 受領者選択(OrderCard / Menu上部 / 卓作成ウィザードの指名)で共有し、
// 注文カードを追加するたびに入れ直さなくて済むようにページ寿命の間だけ保持する(永続化はしない)。
const showNonWorkingUsers = ref(false)

export const useShowNonWorkingUsers = () => ({ showNonWorkingUsers })

const defaultGetRole = user => user?.attributes?.role ?? user?.role

const groupByRole = (users, getRole) => groupBy(users || [], user => getRole(user))

/**
 * ロールごとの受領者候補を返す。
 * - includeNonWorking=false: 出勤者(usersWithTimecard)がいればそのロールは出勤者のみ、いなければ全員(従来の挙動)
 * - includeNonWorking=true : 出勤者を先頭に、続けて非出勤者を並べたロール全員
 *
 * @param {object} args
 * @param {Array} args.users 全ユーザー
 * @param {Array} args.workingUsers 本日のタイムカードを持つユーザー(usersWithTimecard)
 * @param {string[]} args.roles 対象ロール(この順に連結される)
 * @param {boolean} [args.includeNonWorking=false]
 * @param {(user) => string} [args.getRole] ロールの取り出し方(既定は attributes.role → role の順)
 * @returns {Array} 候補ユーザー(ロール順に連結)
 */
export const recipientCandidates = ({
  users,
  workingUsers,
  roles,
  includeNonWorking = false,
  getRole = defaultGetRole,
}) => {
  const usersGrouped = groupByRole(users, getRole)
  const workingGrouped = groupByRole(workingUsers, getRole)

  return flatMap(roles, role => {
    const working = workingGrouped[role] || []
    const all = usersGrouped[role] || []

    if (!includeNonWorking) return working.length > 0 ? working : all

    const workingIds = new Set(working.map(user => String(user.id)))

    return [...working, ...all.filter(user => !workingIds.has(String(user.id)))]
  })
}

/**
 * トグルで表示が変わる(=出勤者絞り込みで隠れている非出勤者がいる)ロールがあるか。
 * 出勤者がいないロールは元々全員表示なのでトグルの対象にならない。
 */
export const hasHiddenNonWorkingUsers = ({
  users,
  workingUsers,
  roles,
  getRole = defaultGetRole,
}) => {
  const usersGrouped = groupByRole(users, getRole)
  const workingGrouped = groupByRole(workingUsers, getRole)

  return roles.some(role => {
    const working = workingGrouped[role] || []
    const all = usersGrouped[role] || []

    return working.length > 0 && all.length > working.length
  })
}
